import React from 'react';
import Button from '../../components/Button/Button';
import { ButtonVariant } from '../../styles/buttons';
import { DeclarationActionTypes } from '../../view-models/DeclarationActionTypes';
import DialogPageViewModel from './types/DialogPageViewModel';
import TextBlockViewModel from '../../view-models/TextBlockViewModel';
import {
  SchemaFrontendUserDataViewModel,
  SchemaParty,
  SchemaUserDeclaration,
} from '../../api/api-schema';
import {
  ConfirmDeclaration,
  WithdrawDeclaration,
} from '../../components/MyDeclaration/DeclarationButtons';
import { CollectionState, ElectionType } from '../../api/api-types';
import {
  buildDialogUrl,
  buildPartyDetailsUrl,
} from '../../utils/dialogFlowUrl';

const fullWidthButtonQuery = '@media (max-width: 47.99em)';
const narrowTabletButtonWidth = {
  width: 'auto',
  [fullWidthButtonQuery]: {
    width: '100%',
  },
};

export enum DialogFlowKind {
  ConfirmTwoDeclarations = 'confirm_two_declarations',
  InitiatePartyWithTwoCollections = 'initiate_party_with_two_collections',
  InitiateBothTypesUserHasDeclaration = 'initiate_both_types_user_has_declaration',
  WithdrawToInitiate = 'withdraw_to_initiate',
  Initiate = 'initiate',
  Confirm = 'confirm',
  Withdraw = 'withdraw',
  Unknown = 'unknown',
}

type DialogFlowStateBase = {
  selectedElectionType?: ElectionType;
  selectedPartyId?: string;
  declarationPartyIdToWithdraw?: string;
  declarationPartyNameToWithdraw?: string;
};

type BaseDialogFlowState = DialogFlowStateBase & {
  kind: Exclude<DialogFlowKind, DialogFlowKind.InitiatePartyWithTwoCollections>;
  canSupportBoth?: never;
};

type InitiatePartyWithTwoCollectionsFlowState = DialogFlowStateBase & {
  kind: DialogFlowKind.InitiatePartyWithTwoCollections;
  canSupportBoth: boolean;
};

export type DialogFlowState =
  | BaseDialogFlowState
  | InitiatePartyWithTwoCollectionsFlowState;

export enum DialogFlowEvent {
  Continue = 'continue',
  Cancel = 'cancel',
}

type DialogFlowTransition = {
  nextState?: DialogFlowKind;
  url?: string;
};

type ChosenElectionType = 'dk' | 'eu' | undefined;
export enum DialogFlowMode {
  Default = 'default',
  SupportBothSequence = 'support_both_sequence',
}

type DialogFlowOptions = {
  hasExplicitElectionType?: boolean;
  mode?: DialogFlowMode;
};

const canUseCollection = (state?: CollectionState | null) =>
  state === CollectionState.running;

const canConfirmDeclaration = (declaration: SchemaUserDeclaration) =>
  declaration.collectionState !== CollectionState.approved &&
  declaration.collectionState !== CollectionState.deactivated &&
  declaration.collectionState !== CollectionState.locked;

export const computeDialogFlowState = (
  declarationType: string | undefined,
  user: SchemaFrontendUserDataViewModel | undefined,
  electionType: number | undefined,
  chosenElectionType: ChosenElectionType,
  partyId: string | undefined,
  currentParty: SchemaParty[] | undefined,
  collectionStates?: Partial<Record<ElectionType, CollectionState>>,
  {
    hasExplicitElectionType = true,
    mode = DialogFlowMode.Default,
  }: DialogFlowOptions = {},
): DialogFlowState => {
  const selectedElectionType =
    chosenElectionType === 'dk'
      ? ElectionType.Folketingsvalg
      : chosenElectionType === 'eu'
      ? ElectionType.EuropaParlamentsvalg
      : undefined;
  const initiateElectionType =
    selectedElectionType !== undefined
      ? selectedElectionType
      : declarationType === DeclarationActionTypes.initiate &&
        electionType !== ElectionType.BothTypes
      ? electionType
      : undefined;
  const declarations = user?.userDeclarations ?? [];
  const requestedPartyIds = partyId?.split(',').filter(Boolean) ?? [];
  const readyToConfirmDeclarations = declarations.filter(
    (declaration) =>
      declaration.daysLeft === 0 &&
      !declaration.isConfirmed &&
      canConfirmDeclaration(declaration) &&
      (!requestedPartyIds.length ||
        requestedPartyIds.includes(declaration.partyId || '')),
  );
  const hasTwoReadyToConfirm = readyToConfirmDeclarations.length === 2;
  const getDeclarationForElection = (type: ElectionType) =>
    declarations.find((declaration) => declaration.electionType === type);
  const declarationsToWithdrawForParty = declarations.filter(
    (declaration) =>
      declaration.partyId !== partyId &&
      (declaration.electionType === ElectionType.Folketingsvalg ||
        declaration.electionType === ElectionType.EuropaParlamentsvalg),
  );
  const canSupportBoth = declarationsToWithdrawForParty.length <= 1;
  const directActionElectionType =
    hasExplicitElectionType &&
    (electionType === ElectionType.Folketingsvalg ||
      electionType === ElectionType.EuropaParlamentsvalg)
      ? electionType
      : undefined;
  const isDirectSinglePartyAction =
    !!partyId &&
    !partyId.includes(',') &&
    directActionElectionType !== undefined;
  const directPartyDeclaration = isDirectSinglePartyAction
    ? declarations.find(
        (declaration) =>
          declaration.partyId === partyId &&
          declaration.electionType === directActionElectionType,
      )
    : undefined;
  const partyElectionTypes = new Set(
    (currentParty ?? [])
      .map((party) => party.type)
      .filter(
        (type): type is ElectionType =>
          type === ElectionType.Folketingsvalg ||
          type === ElectionType.EuropaParlamentsvalg ||
          type === ElectionType.BothTypes,
      ),
  );
  const partySupportsBothTypes =
    partyElectionTypes.has(ElectionType.BothTypes) ||
    (partyElectionTypes.has(ElectionType.Folketingsvalg) &&
      partyElectionTypes.has(ElectionType.EuropaParlamentsvalg));
  const getInitiateFlowForElection = (
    selectedType: ElectionType,
  ): DialogFlowState => {
    const declarationForElection = getDeclarationForElection(selectedType);

    if (partySupportsBothTypes && declarationForElection !== undefined) {
      return {
        kind: DialogFlowKind.InitiateBothTypesUserHasDeclaration,
        selectedElectionType: selectedType,
        declarationPartyIdToWithdraw: declarationForElection.partyId,
        declarationPartyNameToWithdraw:
          declarationForElection.partyName ?? undefined,
      };
    }

    return {
      kind: DialogFlowKind.Initiate,
      selectedElectionType: selectedType,
    };
  };

  if (
    declarationType === DeclarationActionTypes.initiate &&
    isDirectSinglePartyAction &&
    directPartyDeclaration !== undefined
  ) {
    return { kind: DialogFlowKind.Unknown };
  }

  if (
    declarationType === DeclarationActionTypes.confirm &&
    isDirectSinglePartyAction &&
    !readyToConfirmDeclarations.some(
      (declaration) =>
        declaration.partyId === partyId &&
        declaration.electionType === directActionElectionType,
    )
  ) {
    return { kind: DialogFlowKind.Unknown };
  }

  if (
    declarationType === DeclarationActionTypes.withdraw &&
    isDirectSinglePartyAction &&
    directPartyDeclaration === undefined
  ) {
    return { kind: DialogFlowKind.Unknown };
  }

  if (
    declarationType === DeclarationActionTypes.confirm &&
    hasTwoReadyToConfirm &&
    !!partyId &&
    partyId.includes(',')
  ) {
    return { kind: DialogFlowKind.ConfirmTwoDeclarations };
  }

  if (
    declarationType === DeclarationActionTypes.confirm &&
    !!partyId &&
    partyId.includes(',') &&
    readyToConfirmDeclarations.length === 1
  ) {
    return {
      kind: DialogFlowKind.Confirm,
      selectedElectionType: readyToConfirmDeclarations[0].electionType,
      selectedPartyId: readyToConfirmDeclarations[0].partyId,
    };
  }

  if (
    declarationType === DeclarationActionTypes.initiate &&
    electionType === ElectionType.BothTypes &&
    selectedElectionType === undefined
  ) {
    const canInitiateDk = canUseCollection(
      collectionStates?.[ElectionType.Folketingsvalg],
    );
    const canInitiateEu = canUseCollection(
      collectionStates?.[ElectionType.EuropaParlamentsvalg],
    );

    const onlyInitiatableElectionType =
      canInitiateDk && !canInitiateEu
        ? ElectionType.Folketingsvalg
        : !canInitiateDk && canInitiateEu
        ? ElectionType.EuropaParlamentsvalg
        : undefined;

    if (onlyInitiatableElectionType !== undefined) {
      return getInitiateFlowForElection(onlyInitiatableElectionType);
    }

    if (!canInitiateDk && !canInitiateEu) {
      return { kind: DialogFlowKind.Unknown };
    }

    return {
      kind: DialogFlowKind.InitiatePartyWithTwoCollections,
      canSupportBoth,
    };
  }

  if (
    declarationType === DeclarationActionTypes.initiate &&
    electionType === ElectionType.BothTypes &&
    selectedElectionType !== undefined &&
    mode === DialogFlowMode.SupportBothSequence &&
    !canSupportBoth
  ) {
    return { kind: DialogFlowKind.Unknown };
  }

  if (
    declarationType === DeclarationActionTypes.initiate &&
    initiateElectionType !== undefined
  ) {
    if (electionType === ElectionType.BothTypes) {
      const canInitiateSelectedElection = canUseCollection(
        collectionStates?.[initiateElectionType],
      );

      if (!canInitiateSelectedElection) {
        const fallbackElectionType =
          initiateElectionType === ElectionType.Folketingsvalg
            ? ElectionType.EuropaParlamentsvalg
            : ElectionType.Folketingsvalg;

        if (canUseCollection(collectionStates?.[fallbackElectionType])) {
          return getInitiateFlowForElection(fallbackElectionType);
        }

        return { kind: DialogFlowKind.Unknown };
      }

      return getInitiateFlowForElection(initiateElectionType);
    }

    return getInitiateFlowForElection(initiateElectionType);
  }

  if (
    declarationType === DeclarationActionTypes.withdraw &&
    electionType === ElectionType.BothTypes &&
    selectedElectionType !== undefined
  ) {
    return { kind: DialogFlowKind.WithdrawToInitiate, selectedElectionType };
  }

  if (declarationType === DeclarationActionTypes.initiate) {
    return { kind: DialogFlowKind.Initiate, selectedElectionType };
  }

  if (declarationType === DeclarationActionTypes.confirm) {
    return { kind: DialogFlowKind.Confirm };
  }

  if (declarationType === DeclarationActionTypes.withdraw) {
    return { kind: DialogFlowKind.Withdraw };
  }

  return { kind: DialogFlowKind.Unknown };
};

export const getDialogText = (
  model: Partial<DialogPageViewModel> | undefined,
  flowState: DialogFlowState,
  electionType: ElectionType | undefined,
  t: (key: string, options?: Record<string, any>) => string | undefined,
  currentPartyName: string,
  withdrawnPartyName?: string,
): TextBlockViewModel => {
  const emptyTextBlock: TextBlockViewModel = { title: '', text: '' };
  const replacePartyNameTokens = (text: string, partyName: string) =>
    text
      .replace(/\{partyName\}/g, partyName)
      .replace(/\[party name\]/gi, partyName);

  const getElectionTypeName = (type: ElectionType | undefined) => {
    switch (type) {
      case ElectionType.Folketingsvalg:
        return t('dkElection');
      case ElectionType.EuropaParlamentsvalg:
        return t('euElection');
      default:
        return '';
    }
  };

  switch (flowState.kind) {
    case DialogFlowKind.InitiateBothTypesUserHasDeclaration:
      return {
        title: t(
          'dialogPage.title.initiatePartyWithTwoCollectionsUserHasDeclaration',
          {
            partyName:
              flowState.declarationPartyNameToWithdraw || currentPartyName,
            electionType: getElectionTypeName(electionType),
          },
        ),
        text:
          model?.introInitiatePartyWithTwoCollectionsUserHasDeclaration || '',
      };
    case DialogFlowKind.InitiatePartyWithTwoCollections:
      return {
        title: t('dialogPage.title.initiatePartyWithTwoCollections', {
          partyName: currentPartyName,
          electionType: getElectionTypeName(electionType),
        }),
        text: model?.introInitiatePartyWithTwoCollections || '',
      };
    case DialogFlowKind.Initiate:
      return {
        title: t('dialogPage.title.initiate', {
          partyName: currentPartyName,
          electionType: getElectionTypeName(electionType),
        }),
        text: model?.introInitiate || '',
      };
    case DialogFlowKind.ConfirmTwoDeclarations:
      return {
        title: t('dialogPage.title.confirmTwoDeclarations'),
        text: model?.introConfirmTwoDeclarations || '',
      };
    case DialogFlowKind.Confirm:
      return {
        title: t('dialogPage.title.confirm', {
          partyName: currentPartyName,
          electionType: getElectionTypeName(electionType),
        }),
        text: model?.introConfirm || '',
      };
    case DialogFlowKind.Withdraw:
      return {
        title: t('dialogPage.title.withdraw', {
          partyName: currentPartyName,
          electionType: getElectionTypeName(electionType),
        }),
        text: model?.introWithdraw || '',
      };
    case DialogFlowKind.WithdrawToInitiate:
      return {
        title: t('dialogPage.title.withdrawToInitiate', {
          partyName: withdrawnPartyName || currentPartyName,
          electionType: getElectionTypeName(electionType),
        }),
        text: replacePartyNameTokens(
          model?.introWithdrawToInitiate || '',
          currentPartyName,
        ),
      };
    default:
      return emptyTextBlock;
  }
};

export const getButton = (
  model: Partial<DialogPageViewModel> | undefined,
  flowState: DialogFlowState,
  electionType: ElectionType | undefined,
  partyId: string | null | undefined,
  partyDetailsUrl: string | undefined,
  dialogPageUrl: string | undefined,
  confirmReturnUrlOverride?: string,
  isBothFlow?: boolean,
): React.ReactNode | React.ReactNode[] | undefined => {
  const typedElectionType =
    flowState.selectedElectionType !== undefined
      ? flowState.selectedElectionType
      : electionType;
  const typedPartyId = flowState.selectedPartyId || partyId;
  const partyReturnUrl = buildPartyDetailsUrl(
    partyDetailsUrl,
    typedPartyId,
    typedElectionType,
  );
  const bothFlowChoice: 'dk' | 'eu' =
    typedElectionType === ElectionType.Folketingsvalg ? 'dk' : 'eu';

  const transitionContext = {
    partyId: typedPartyId,
    typedElectionType,
    partyReturnUrl,
    dialogPageUrl,
    isBothFlow,
    bothFlowChoice,
    confirmReturnUrlOverride,
    declarationPartyIdToWithdraw: flowState.declarationPartyIdToWithdraw,
    declarationPartyNameToWithdraw: flowState.declarationPartyNameToWithdraw,
  };

  const transitionMap: Record<
    DialogFlowKind,
    Partial<
      Record<
        DialogFlowEvent,
        (ctx: typeof transitionContext) => DialogFlowTransition
      >
    >
  > = {
    [DialogFlowKind.ConfirmTwoDeclarations]: {},
    [DialogFlowKind.InitiatePartyWithTwoCollections]: {},
    [DialogFlowKind.InitiateBothTypesUserHasDeclaration]: {
      [DialogFlowEvent.Continue]: (ctx) => ({
        nextState: DialogFlowKind.WithdrawToInitiate,
        url: buildDialogUrl(ctx.dialogPageUrl, {
          type: DeclarationActionTypes.withdraw,
          electionType: ElectionType.BothTypes,
          party: ctx.partyId ?? undefined,
          choice: ctx.bothFlowChoice,
          extraParams: {
            ...(ctx.isBothFlow ? { bothFlow: '1' } : {}),
            ...(ctx.declarationPartyIdToWithdraw
              ? { withdrawParty: ctx.declarationPartyIdToWithdraw }
              : {}),
            ...(ctx.declarationPartyNameToWithdraw
              ? { withdrawPartyName: ctx.declarationPartyNameToWithdraw }
              : {}),
          },
        }),
      }),
    },
    [DialogFlowKind.WithdrawToInitiate]: {
      [DialogFlowEvent.Continue]: (ctx) => ({
        nextState: DialogFlowKind.Initiate,
        url: buildDialogUrl(ctx.dialogPageUrl, {
          type: DeclarationActionTypes.initiate,
          electionType: ctx.isBothFlow
            ? ElectionType.BothTypes
            : ctx.typedElectionType,
          party: ctx.partyId ?? undefined,
          choice: ctx.isBothFlow ? ctx.bothFlowChoice : undefined,
          extraParams: ctx.isBothFlow ? { bothFlow: '1' } : undefined,
        }),
      }),
    },
    [DialogFlowKind.Initiate]: {},
    [DialogFlowKind.Confirm]: {
      [DialogFlowEvent.Continue]: (ctx) => ({
        url: ctx.confirmReturnUrlOverride || ctx.partyReturnUrl,
      }),
    },
    [DialogFlowKind.Withdraw]: {
      [DialogFlowEvent.Continue]: (ctx) => ({
        url: ctx.partyReturnUrl,
      }),
    },
    [DialogFlowKind.Unknown]: {},
  };

  const continueTransition = transitionMap[flowState.kind]?.[
    DialogFlowEvent.Continue
  ]?.(transitionContext);

  switch (flowState.kind) {
    case DialogFlowKind.ConfirmTwoDeclarations:
    case DialogFlowKind.InitiatePartyWithTwoCollections:
      return undefined;
    case DialogFlowKind.InitiateBothTypesUserHasDeclaration:
      return (
        <WithdrawDeclaration
          partyId={flowState.declarationPartyIdToWithdraw || ''}
          electionType={typedElectionType as ElectionType}
          label={model?.buttonInitiatePartyWithTwoCollectionsUserHasDeclaration}
          returnUrl={continueTransition?.url}
          cancelRedirectUrl={partyReturnUrl}
        />
      );
    case DialogFlowKind.Initiate:
      return (
        <Button variant={ButtonVariant.Primary} sx={{ width: 'auto' }}>
          {model?.buttonInitiate}
        </Button>
      );
    case DialogFlowKind.Confirm:
      return (
        <ConfirmDeclaration
          partyId={typedPartyId || ''}
          electionType={typedElectionType as ElectionType}
          label={model?.buttonConfirm}
          returnUrl={continueTransition?.url}
          cancelRedirectUrl={partyReturnUrl}
        />
      );
    case DialogFlowKind.Withdraw:
      return (
        <WithdrawDeclaration
          partyId={typedPartyId || ''}
          electionType={typedElectionType as ElectionType}
          label={model?.buttonWithdraw}
          returnUrl={continueTransition?.url}
        />
      );
    case DialogFlowKind.WithdrawToInitiate:
      return (
        <Button
          variant={ButtonVariant.Primary}
          href={continueTransition?.url}
          sx={{ width: 'auto' }}
        >
          {model?.buttonWithdrawToInitiate}
        </Button>
      );
    default:
      return undefined;
  }
};

export const getCancelButton = (
  model: Partial<DialogPageViewModel> | undefined,
  flowState: DialogFlowState,
  partyId?: string | null,
  electionType?: number | undefined,
  partyDetailsUrl?: string | undefined,
): React.ReactNode | undefined => {
  const transitionMap: Record<
    DialogFlowKind,
    Partial<Record<DialogFlowEvent, () => DialogFlowTransition>>
  > = {
    [DialogFlowKind.InitiateBothTypesUserHasDeclaration]: {
      [DialogFlowEvent.Cancel]: () => ({
        url: buildPartyDetailsUrl(
          partyDetailsUrl,
          partyId,
          electionType as ElectionType | undefined,
        ),
      }),
    },
    [DialogFlowKind.WithdrawToInitiate]: {
      [DialogFlowEvent.Cancel]: () => ({
        url: buildPartyDetailsUrl(
          partyDetailsUrl,
          partyId,
          electionType as ElectionType | undefined,
        ),
      }),
    },
    [DialogFlowKind.ConfirmTwoDeclarations]: {},
    [DialogFlowKind.InitiatePartyWithTwoCollections]: {},
    [DialogFlowKind.Initiate]: {},
    [DialogFlowKind.Confirm]: {
      [DialogFlowEvent.Cancel]: () => ({
        url: buildPartyDetailsUrl(
          partyDetailsUrl,
          partyId,
          electionType as ElectionType | undefined,
        ),
      }),
    },
    [DialogFlowKind.Withdraw]: {
      [DialogFlowEvent.Cancel]: () => ({
        url: buildPartyDetailsUrl(
          partyDetailsUrl,
          partyId,
          electionType as ElectionType | undefined,
        ),
      }),
    },
    [DialogFlowKind.Unknown]: {},
  };

  const cancelTransition = transitionMap[flowState.kind]?.[
    DialogFlowEvent.Cancel
  ]?.();
  const onCancel = () => {
    const redirectUrl = cancelTransition?.url;
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.history.back();
    }
  };

  if (flowState.kind === DialogFlowKind.InitiateBothTypesUserHasDeclaration) {
    return (
      <Button
        variant={ButtonVariant.Flat}
        sx={narrowTabletButtonWidth}
        href={cancelTransition?.url}
        onClick={cancelTransition?.url ? undefined : onCancel}
      >
        {model?.cancelButtonInitiatePartyWithTwoCollectionsUserHasDeclaration}
      </Button>
    );
  }

  if (flowState.kind === DialogFlowKind.WithdrawToInitiate) {
    return (
      <Button
        variant={ButtonVariant.Flat}
        sx={narrowTabletButtonWidth}
        href={cancelTransition?.url}
        onClick={cancelTransition?.url ? undefined : onCancel}
      >
        {model?.cancelButtonWithdrawToInitiate}
      </Button>
    );
  }

  return model?.cancelButton ? (
    <Button
      variant={ButtonVariant.Flat}
      sx={narrowTabletButtonWidth}
      href={cancelTransition?.url}
      onClick={cancelTransition?.url ? undefined : onCancel}
    >
      {model?.cancelButton}
    </Button>
  ) : undefined;
};

export const getDialogImage = (
  model: Partial<DialogPageViewModel> | undefined,
  flowState: DialogFlowState,
): DialogPageViewModel['imageInitiate'] => {
  switch (flowState.kind) {
    case DialogFlowKind.InitiateBothTypesUserHasDeclaration:
      return model?.imageInitiatePartyWithTwoCollectionsUserHasDeclaration;
    case DialogFlowKind.InitiatePartyWithTwoCollections:
      return model?.imageInitiatePartyWithTwoCollections;
    case DialogFlowKind.WithdrawToInitiate:
      return model?.imageWithdrawToInitiate;
    case DialogFlowKind.ConfirmTwoDeclarations:
      return model?.imageConfirmTwoDeclarations;
    case DialogFlowKind.Confirm:
      return model?.imageConfirm;
    case DialogFlowKind.Withdraw:
      return model?.imageWithdraw;
    case DialogFlowKind.Initiate:
    case DialogFlowKind.Unknown:
    default:
      return model?.imageInitiate;
  }
};
